/**
 * Order Summary dashboard widget (like Spending Summary).
 * Total revenue (all time) and top sold products. Click navigates to /orders.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getRevenueSummary } from "../../services/revenueService";
import { getSalesVelocityReport } from "../../services/reportsService";
import { formatCurrency } from "../../utils/currency";
import type { RevenueSummary as RevenueSummaryType } from "../../types/models";
import { getApiErrorMessage } from "../../utils/apiError";

export function OrderSummary() {
  const { t } = useTranslation("orders");
  const [summary, setSummary] = useState<RevenueSummaryType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.allSettled([getRevenueSummary(), getSalesVelocityReport()])
      .then(([revenueResult, salesVelocityResult]) => {
        if (cancelled) return;

        // Revenue summary is required for this widget.
        if (revenueResult.status !== "fulfilled") {
          throw revenueResult.reason;
        }

        const revenueSummary = revenueResult.value;
        const salesVelocityProducts =
          salesVelocityResult.status === "fulfilled"
            ? (salesVelocityResult.value.top_products_by_units ?? []).map((p) => ({
                product_name: p.product_name,
                quantity_sold: p.units_sold,
                revenue: Number(p.revenue ?? 0),
              }))
            : [];

        setSummary({
          ...revenueSummary,
          // Prefer Sales Velocity aggregation when available, but gracefully
          // fallback to /revenue/summary data if that endpoint is unavailable.
          top_sold_products:
            salesVelocityProducts.length > 0
              ? salesVelocityProducts
              : (revenueSummary.top_sold_products ?? []),
        });
      })
      .catch((err) => {
        if (!cancelled) {
          const msg = getApiErrorMessage(err, t("summary_error_loading"));
          setError(msg);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleClick = () => navigate("/orders");

  if (loading) {
    return (
      <button
        type="button"
        onClick={handleClick}
        className="w-full text-left flex flex-col items-center justify-center py-8 text-center hover:opacity-90 transition-opacity"
      >
        <div className="w-8 h-8 border-2 border-brand-teal border-t-transparent rounded-full animate-spin mb-3" />
        <p className="text-sm text-neutral-500 dark:text-neutral-400">{t("summary_loading")}</p>
      </button>
    );
  }

  if (error) {
    return (
      <button
        type="button"
        onClick={handleClick}
        className="w-full text-left py-4 text-center hover:opacity-90 transition-opacity"
      >
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      </button>
    );
  }

  if (!summary) {
    return (
      <button
        type="button"
        onClick={handleClick}
        className="w-full text-left py-4 text-center hover:opacity-90 transition-opacity"
      >
        <p className="text-sm text-neutral-500 dark:text-neutral-400">{t("summary_no_data")}</p>
      </button>
    );
  }

  const totalRevenue = Number(summary.total_revenue ?? 0);
  const topProducts = (summary.top_sold_products ?? []).slice(0, 5);

  return (
    <button
      type="button"
      onClick={handleClick}
      className="w-full text-left hover:opacity-90 transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-teal focus-visible:ring-offset-2 rounded-lg"
    >
      <div className="space-y-3">
        <div className="flex justify-between items-baseline gap-2">
          <span className="text-xs text-neutral-500 dark:text-neutral-400">
            {t("summary_total_revenue")}
          </span>
          <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 tabular-nums">
            {formatCurrency(totalRevenue)}
          </span>
        </div>
        {topProducts.length > 0 && (
          <div className="border-t border-neutral-200 dark:border-neutral-700 pt-2">
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-1.5">
              {t("summary_top_sold")}
            </p>
            <ul className="space-y-1.5 text-sm">
              {topProducts.map((p, i) => (
                <li
                  key={`${i}-${p.product_name}`}
                  className="flex items-center justify-between gap-2 text-neutral-700 dark:text-neutral-300"
                  title={`${p.product_name} - ${t("summary_qty")}: ${p.quantity_sold} - ${t("summary_revenue")}: ${formatCurrency(
                    Number(p.revenue ?? 0)
                  )}`}
                >
                  <span className="min-w-0 flex items-center gap-1.5">
                    <span className="truncate" title={p.product_name}>
                      {p.product_name}
                    </span>
                    <span className="shrink-0 text-neutral-500 dark:text-neutral-400">
                      ×{p.quantity_sold}
                    </span>
                  </span>
                  <span className="tabular-nums font-medium shrink-0">
                    {formatCurrency(Number(p.revenue ?? 0))}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </button>
  );
}
